import { getCriminalRecordDownloadUrl, uploadCriminalRecord } from '@/services/users.service';
import { getErrorMessage } from '@/utils/errors-utils';
import type { CriminalRecordUploadInfo } from '@/types/user.types';

/** Dedicated-table row used by {@link resolveCriminalRecordDisplay}. */
type CriminalRecordApiDisplay = {
  status?: string;
  fileName?: string;
  depositedAt?: string;
  validatedAt?: string | null;
  validatedByFullName?: string | null;
  rejectedAt?: string | null;
  rejectedByFullName?: string | null;
};

/** Onboarding `criminalRecord` bag used by {@link resolveCriminalRecordDisplay}. */
type CriminalRecordSectionDisplay = {
  validationStatus?: string;
  status?: string;
  documentLink?: string;
  documentName?: string;
  fileName?: string;
};

/** Folder-level `record.validation` used by {@link resolveCriminalRecordDisplay}. */
type CriminalRecordFolderValidation = {
  validatedAt?: { $date?: string } | string | null;
  validatedBy?: { userFullName?: string | null };
};

/** Inputs for {@link resolveCriminalRecordDisplay}. */
type ResolveCriminalRecordDisplayParams = {
  criminalRecord?: CriminalRecordApiDisplay | null;
  sectionCriminalRecord?: CriminalRecordSectionDisplay | null;
  recordValidation?: CriminalRecordFolderValidation | null;
};

/** Merged criminal-record fields for tutor detail tabs. */
type CriminalRecordDisplay = {
  status: string;
  validatedAt: string | null;
  validatedBy: string | null;
  rejectedAt: string | null;
  rejectedBy: string | null;
  hasPdf: boolean;
};

/** Form patch for a criminal-record upload (or clear when `info` is null). */
export function buildCriminalRecordPatch(
  info: {
    fileName: string;
    depositedAt: string;
    status: string;
  } | null,
): Record<string, string> {
  if (info) {
    return {
      'criminalRecord.fileName': info.fileName,
      'criminalRecord.documentName': info.fileName,
      'criminalRecord.uploadDate': info.depositedAt.slice(0, 10),
      'criminalRecord.status': info.status,
      criminalRecordDocumentLink: info.fileName,
    };
  }
  return {
    'criminalRecord.fileName': '',
    'criminalRecord.documentName': '',
    criminalRecordDocumentLink: '',
  };
}

/** Signed download URL, falling back to a stored non-`stored` link. */
export async function fetchCriminalRecordSignedUrl(
  tutorUserId: string,
  documentLink?: string,
): Promise<string | null> {
  if (!tutorUserId) {
    if (documentLink && documentLink !== 'stored') return documentLink;
    return null;
  }
  try {
    const url = await getCriminalRecordDownloadUrl(tutorUserId);
    if (url) return url;
    if (documentLink && documentLink !== 'stored') return documentLink;
    return null;
  } catch {
    return null;
  }
}

/** Upload a criminal-record file for a tutor. */
export function postCriminalRecordFile(
  tutorUserId: string,
  file: File,
): Promise<CriminalRecordUploadInfo> {
  if (!tutorUserId) throw new Error(getErrorMessage('missingTutorId'));
  return uploadCriminalRecord(tutorUserId, file);
}

/** True when issuer, number, date, file, and honor commitment are all set. */
export function isCriminalRecordMetadataComplete(
  criminalRecord: Record<string, unknown> | undefined,
): boolean {
  if (!criminalRecord) return false;
  return !!(
    (criminalRecord.fileName || criminalRecord.documentName) &&
    criminalRecord.issuedBy &&
    criminalRecord.recordNumber &&
    criminalRecord.issueDate &&
    criminalRecord.honorCommitment === true
  );
}

/** Map a stored criminal-record bag to upload-info, or null if no file. */
export function toCriminalRecordUploadInfo(
  criminalRecord: Record<string, unknown>,
): CriminalRecordUploadInfo | null {
  const fileName = typeof criminalRecord.fileName === 'string' ? criminalRecord.fileName : '';
  if (!fileName) return null;
  return {
    fileName,
    depositedAt:
      typeof criminalRecord.uploadDate === 'string'
        ? criminalRecord.uploadDate
        : new Date().toISOString(),
    status: criminalRecord.status === 'validated' ? 'validated' : 'pending',
    validatedAt: typeof criminalRecord.validatedAt === 'string' ? criminalRecord.validatedAt : null,
  };
}

/** Merge API criminal record, onboarding section, and `record.validation` for the detail tabs. */
export function resolveCriminalRecordDisplay(
  params: ResolveCriminalRecordDisplayParams,
): CriminalRecordDisplay {
  const recordValidatedAt = params.recordValidation?.validatedAt;
  return {
    status:
      params.criminalRecord?.status ??
      params.sectionCriminalRecord?.validationStatus ??
      params.sectionCriminalRecord?.status ??
      'pending',
    validatedAt:
      params.criminalRecord?.validatedAt ??
      (recordValidatedAt && typeof recordValidatedAt === 'object'
        ? recordValidatedAt.$date
        : undefined) ??
      (recordValidatedAt as string | null | undefined) ??
      null,
    validatedBy:
      params.criminalRecord?.validatedByFullName ??
      params.recordValidation?.validatedBy?.userFullName ??
      null,
    rejectedAt: params.criminalRecord?.rejectedAt ?? null,
    rejectedBy: params.criminalRecord?.rejectedByFullName ?? null,
    hasPdf: !!(
      params.sectionCriminalRecord?.documentLink ||
      params.sectionCriminalRecord?.documentName ||
      params.sectionCriminalRecord?.fileName ||
      params.criminalRecord?.fileName
    ),
  };
}

/** File name from the dedicated row, then the onboarding section. */
export function resolveCriminalRecordFileName(
  apiFileName: string | undefined,
  section: CriminalRecordSectionDisplay | null | undefined,
): string {
  return apiFileName || section?.fileName || section?.documentName || 'casier-judiciaire.pdf';
}
